import { ExtendedError } from './ExtendedError'
import { IncenseError } from './IncenseError'
import type { BaseLogger, ErrorMessageProps, StagedLoggingMetadata, UnknownRecord } from './types'
import { serializeObject } from './utils/serializeObject'

/**
 * @class Incense - A lightweight interface for structured error handling and logging
 *
 * @public {BaseLogger} logger - Logging logger implementing standard log level methods
 * @private {string} logPrefix - Optional prefix appended to error names for grouping
 * @private {ExtendedError} errorObject - Composed error capturing name, message, and statusCode
 * @private {StagedLoggingMetadata} stagedLoggingMetadata - Aggregated safe/sensitive metadata and previous error
 */
export class Incense {
  public logger: BaseLogger
  private logPrefix: string
  private errorObject!: ExtendedError
  private stagedLoggingMetadata: StagedLoggingMetadata

  /**
   * Creates an instance of `Incense`
   *
   * @param {BaseLogger} logger - Dependency-injected logger instance (e.g. `Rollbar`, `Sentry`)
   * @param {string} [logPrefix] - Optional string prepended to all error names
   * @param {unknown} [previousError] - Previously thrown error to chain with the new one
   */
  constructor(logger: BaseLogger, logPrefix?: string, previousError?: unknown) {
    this.logger = logger
    this.logPrefix = logPrefix ?? ''

    this.stagedLoggingMetadata = {
      safe: {},
      sensitive: {},
      error: previousError ?? null,
    }
  }

  //--------[DATA AGGREGATION METHODS]--------//
  /**
   * Stores core error details by constructing an `ExtendedError`
   *
   * @example
   * incense.captureDetails({
   *   name: 'NetworkError',
   *   message: 'Failed to fetch user profile',
   *   statusCode: 503,
   * })
   *
   * @param {ErrorMessageProps} params - Props used to build the `ExtendedError`
   * @returns {Incense} Current instance for chaining
   */
  captureDetails({ name, message, statusCode }: ErrorMessageProps): Incense {
    const nameWithPrefix = [this.logPrefix, name].filter(Boolean).join(' ')
    this.errorObject = new ExtendedError(nameWithPrefix, message, statusCode)
    return this
  }

  /**
   * Adds non-PII metadata that can be transmitted in clear-text
   *
   * @param {UnknownRecord} safeData - Arbitrary safe metadata
   * @returns {Incense} Current instance for chaining
   */
  setSafeData(safeData: UnknownRecord): Incense {
    this.stagedLoggingMetadata.safe = safeData
    return this
  }

  /**
   * Adds sensitive metadata that will be serialized and encrypted before transport
   *
   * @param {UnknownRecord} sensitiveData - Sensitive metadata needing encryption
   * @returns {Incense} Current instance for chaining
   */
  setSensitiveData(sensitiveData: UnknownRecord): Incense {
    this.stagedLoggingMetadata.sensitive = sensitiveData
    return this
  }

  //--------[LOG LEVEL METHODS]--------//
  /**
   * Sends the aggregated error details via `logger.error()`
   *
   * @returns {Promise<Incense>} Promise resolving to current instance for chaining
   */
  async sendError(): Promise<Incense> {
    const result = this.logger.error(this.errorObject, this.gatherMetadata())
    if (result instanceof Promise) {
      await result
    }
    return this
  }

  /**
   * Sends the aggregated error details via `logger.log()`
   *
   * @returns {Promise<Incense>} Promise resolving to current instance for chaining
   */
  async sendLog(): Promise<Incense> {
    const result = this.logger.log(this.errorObject, this.gatherMetadata())
    if (result instanceof Promise) {
      await result
    }
    return this
  }

  /**
   * Sends the aggregated error details via `logger.warn()`
   *
   * @returns {Promise<Incense>} Promise resolving to current instance for chaining
   */
  async sendWarn(): Promise<Incense> {
    const result = this.logger.warn(this.errorObject, this.gatherMetadata())
    if (result instanceof Promise) {
      await result
    }
    return this
  }

  /**
   * Sends the aggregated error details via `logger.info()`
   *
   * @returns {Promise<Incense>} Promise resolving to current instance for chaining
   */
  async sendInfo(): Promise<Incense> {
    const result = this.logger.info(this.errorObject, this.gatherMetadata())
    if (result instanceof Promise) {
      await result
    }
    return this
  }

  /**
   * Sends the aggregated error details via `logger.debug()`
   *
   * @returns {Promise<Incense>} Promise resolving to current instance for chaining
   */
  async sendDebug(): Promise<Incense> {
    const result = this.logger.debug(this.errorObject, this.gatherMetadata())
    if (result instanceof Promise) {
      await result
    }
    return this
  }

  //--------[HELPER METHODS]--------//
  /**
   * Rethrows the captured error wrapped in an `IncenseError` in order to bubble up the stack
   *
   * @throws {IncenseError}
   */
  rethrow(): void {
    const currentError = this.errorObject
    const previousError = this.stagedLoggingMetadata.error

    if (!previousError) {
      throw new IncenseError(currentError)
    }

    throw new IncenseError(currentError, previousError)
  }

  /**
   * Stages a previously thrown error so it can be included in the sensitive metadata
   *
   * @param {unknown} previousError - Prior error caught higher up the stack
   * @returns {Incense} Current instance for chaining
   */
  setPreviousError(previousError: unknown): Incense {
    this.stagedLoggingMetadata.error = previousError
    return this
  }

  /**
   * Compiles safe and sensitive metadata into the final object passed to the logger
   *
   * @private
   * @returns {Record<string, any>} Prepared metadata to attach to logger calls
   *
   * @example
   * {
   *   applicationId: 'abc123',
   *   questionnaireSlug: 'ntl',
   *   sensitive: {
   *     error: Error,
   *     ssn: '333-222-111',
   *   }
   * }
   */
  private gatherMetadata(): UnknownRecord {
    const sensitive: UnknownRecord = this.stagedLoggingMetadata.sensitive

    if (this.stagedLoggingMetadata.error) {
      sensitive['error'] = this.stagedLoggingMetadata.error
    }

    return {
      ...this.stagedLoggingMetadata.safe,
      ...{ sensitive: serializeObject(sensitive) },
    }
  }
}
